import React from "react";
import type { Tournament, JoinedTournament } from "@/shared/types";

function JoinLeaveModal<T extends Tournament | JoinedTournament>({ accept, closeModal, selectedSession } : { accept: () => void, closeModal: () => void, selectedSession: T | null }){
    // Helper to get name regardless of type
    const getName = () => {
        if (!selectedSession) return '';
        return 'Name' in selectedSession ? selectedSession.Name : 'name' in selectedSession ? selectedSession.name : '';
    };

    // Helper to get isJoined regardless of type
    const getIsJoined = () => {
        if (!selectedSession) return false;
        return 'IsJoined' in selectedSession ? selectedSession.IsJoined : 'isJoined' in selectedSession ? selectedSession.isJoined : false;
    };

    // Helper to get prize/pot regardless of type
    const getPrize = () => {
        if (!selectedSession) return 0;
        if ('Prize' in selectedSession) return selectedSession.Prize;
        if ('prize' in selectedSession) return selectedSession.prize;
        return 0;
    };

    // Helper to check if we have time/date info
    const hasTimeInfo = () => {
        return selectedSession && 'Time' in selectedSession && 'Date' in selectedSession;
    };

    return(
          <div className="overlay">
            <div>
              <div style={{ fontSize: '10px', padding: '0 10px' }}>
                Are you sure you want to {getIsJoined() ? 'leave' : 'join'} tournament{' '}
                <strong>{getName()}</strong>
                {hasTimeInfo() && (
                    <>
                        {' '}at{' '}
                        <strong>{formatToLocalMeridian((selectedSession as Tournament).Date, (selectedSession as Tournament).Time)}</strong>
                        {' '}on{' '}
                        <strong>{formatToShortDate((selectedSession as Tournament).Date)}</strong>
                    </>
                )}
                {' '}for a payout of{' '}
                <strong>${getPrize()}</strong>?
              </div>
              <div className="confirmationButtonsContainer" style={{ display: 'flex' }}>
                <button
                  onClick={closeModal}
                  className="btn btn-danger"
                >
                  No, cancel
                </button>
                <button
                  data-cy="confirm-yes-btn"
                  onClick={() => {accept()}}
                  className="btn btn-primary"
                >
                  Yes, {getIsJoined() ? 'leave' : 'join'}
                </button>
              </div>
            </div>
          </div>
    )
}

function formatToShortDate(dateStr: string | undefined): string {
  if (!dateStr) return ''
  const date = new Date(dateStr);
  const month = date.getMonth() + 1; // Months are zero-indexed
  const day = date.getDate();
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
}

function formatToLocalMeridian(dateStr: string | undefined, time24: string | undefined): string {
  if (!dateStr || !time24) return '';

  // Parse UTC date and time, then convert to local timezone
  const dateOnly = dateStr.split('T')[0];
  const [year, month, day] = dateOnly.split('-').map(Number);
  const [hourStr, minuteStr] = time24.split(':');
  const hour = parseInt(hourStr, 10);
  const minute = parseInt(minuteStr, 10);

  // Create Date in UTC
  const utcDate = new Date(Date.UTC(year, month - 1, day, hour, minute));

  // Get local hours and minutes
  const localHour = utcDate.getHours();
  const localMinute = utcDate.getMinutes();

  const ampm = localHour >= 12 ? 'PM' : 'AM';
  const formattedHour = localHour % 12 === 0 ? 12 : localHour % 12;
  return `${formattedHour}:${localMinute.toString().padStart(2, '0')} ${ampm}`;
}

export default JoinLeaveModal
